import { GlobalPositionStrategy, Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector } from '@angular/core';

import { IrisFullScreenModalComponent } from '../full-screen/full-screen-modal.component';
import { IrisModalComponent } from '../modal.component';

import { IIrisModalConfig } from './modal-config.interface';
import { IrisModalContent } from './modal-content.type';
import { IrisModalRef } from './modal-ref';
import { IrisModalSize } from './modal-size.enum';

@Injectable({
  providedIn: 'root',
})
export class IrisModalService {
  constructor(private overlay: Overlay, private injector: Injector) {}

  /**
   * Creates a modal with set padding and heading with a title and close button.
   * @param content The component or template that will be displayed in the modal.
   * @param config Includes configuration data that the modal content can access,
   *   the modal title, and the desired modal size.
   * @returns a reference to the modal that can be used to close it and/or receive data
   *   from the closed modal.
   */
  open<TContent, TModalInputs, TResponseData>(
    content: IrisModalContent<TContent>,
    config: IIrisModalConfig<TModalInputs> = { size: IrisModalSize.SM, title: '' }
  ): IrisModalRef<TContent, TModalInputs, TResponseData> {
    return this.openModal(
      content,
      config,
      this.getPanelClasses(config.size, config)
    );
  }

  /**
   * Creates a full-screen overlay at all window sizes, with no pre-configured styling.
   * @param content The component or template that will be displayed in the modal.
   * @param data The configuration data that the modal content can access.
   * @param customPanelClasses Optional array of custom CSS classes to apply to the overlay panel.
   * @returns a reference to the modal that can be used to close it and/or receive data
   *   from the closed modal.
   */
  openUnstyledFullScreenOverlay<TContent, TModalInputs, TResponseData>(
    content: IrisModalContent<TContent>,
    data: TModalInputs,
    customPanelClasses: string[] = []
  ): IrisModalRef<TContent, TModalInputs, TResponseData> {
    return this.openModal(
      content,
      { data },
      this.getPanelClasses().concat(['iris-overlay-panel-full-screen']).concat(customPanelClasses)
    );
  }

  private openModal<TContent, TModalInputs, TResponseData>(
    content: IrisModalContent<TContent>,
    config: IIrisModalConfig<TModalInputs>,
    overlayPanelClasses: string[]
  ): IrisModalRef<TContent, TModalInputs, TResponseData> {
    // Create the overlay with customizable options
    const overlayRef = this.overlay.create(
      new OverlayConfig({
        backdropClass: [
          'iris-overlay-backdrop',
          'iris-overlay-backdrop-showing',
          'iris-overlay-backdrop-styles'
        ],
        hasBackdrop: true,
        positionStrategy: this.getPositionStrategy(config),
        panelClass: overlayPanelClasses,
        scrollStrategy: this.overlay.scrollStrategies.block()
      })
    );

    const irisModalRef = new IrisModalRef<TContent, TModalInputs, TResponseData>(
      overlayRef,
      content,
      config
    );

    // Create injector to be able to reference the DialogRef from within the component
    const injector = Injector.create({
      parent: this.injector,
      providers: [{ provide: IrisModalRef, useValue: irisModalRef }],
    });

    const modalComponent = config.size
      ? IrisModalComponent
      : IrisFullScreenModalComponent;

    // Attach component portal to the overlay
    const portal = new ComponentPortal(
      modalComponent,
      null,
      injector
    );
    overlayRef.attach(portal);

    return irisModalRef;
  }

  private getPanelClasses(
    size?: IrisModalSize,
    config?: IIrisModalConfig<unknown>
  ): string[] {
    // Multiple classes are necessary to enable CSS selectors with sufficient precedence
    const panelClasses = ['iris-overlay-panel', 'iris-overlay-panel-styles'];

    if (size === IrisModalSize.LG) {
      panelClasses.push('iris-overlay-panel-lg');
    } else if (size === IrisModalSize.SM) {
      panelClasses.push('iris-overlay-panel-small');
    }

    if (config?.openAsDrawer) {
      panelClasses.push('iris-overlay-panel-drawer');
      if (config.drawerSide === 'left') {
        panelClasses.push('iris-overlay-panel-drawer-left');
      } else {
        panelClasses.push('iris-overlay-panel-drawer-right');
      }
      return panelClasses;
    }

    return panelClasses;
  }

  private getPositionStrategy(config?: IIrisModalConfig<unknown>): GlobalPositionStrategy {
    // Drawer positions to the chosen side; otherwise center.
    if (config?.openAsDrawer) {
      const side = config.drawerSide === 'left' ? 'left' : 'right';
      const strategy = this.overlay.position().global().top('0').bottom('0');
      return side === 'left' ? strategy.left('0') : strategy.right('0');
    }
    // Globally centered position strategy
    return this.overlay.position().global().centerHorizontally().centerVertically();
  }
}
